import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRelayCachePreheatMode} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {ReactNode, RefObject} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import {useIssueHeaderDataContext} from '../header/IssueHeaderDataContext'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import type {
  IssueTimelineIssueFragment$data,
  IssueTimelineIssueFragment$key,
} from './__generated__/IssueTimelineIssueFragment.graphql'
import type {EventProps, IssueTimelineItemFragment} from './IssueTimelineItem'
import type {LoadMoreCallbackFn} from './LoadMore'
import {TimelinePanel} from './TimelinePanel'
import {useTimelineItems} from './use-timeline-items'
import type {UseTimelineItemsCoreResult} from './use-timeline-items-core'
import {useTimelineItemsTanstack} from './use-timeline-items-tanstack'

type LoadItem = {
  type: 'load'
  position: 'top' | 'bottom'
  loadFromTop: LoadMoreCallbackFn
  loadFromBottom: LoadMoreCallbackFn
  numberOfRemainingItems: number
  isCurrentlyLoading?: boolean
}

type EventItem = {type: 'event'} & RolledUpTimelineItem<IssueTimelineItemFragment>
export type TimelineItem = EventItem | LoadItem

/**
 * Temporary type for parsing the timeline items and grouping them into section elements
 * for improve the accessibility of the event items by adding landmarks for navigation.
 */
export type RenderItem = {
  /**
   * Controls whether the item has already been added to the grouped events or not.
   *
   * Only used in events and not in load items, graceful degratation items or issue comments.
   */
  isAddedToGroupedEvents: boolean
  timelineItem: TimelineItem
  render: ReactNode
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const mainTimelineFragment = graphql`
  fragment IssueTimelineIssueFragment on Issue
  @argumentDefinitions(skip: {type: "Int"}, count: {type: "Int", defaultValue: 15}) {
    id
    url
    repository {
      id
      nameWithOwner
      visibility
      viewerCanPush
    }
    ...useTimelineItemsFrontFragment @arguments(count: $count, skip: $skip)
    ...useTimelineItemsBackFragment
  }
`

export type IssueTimelineSecondaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'isTransferInProgress'>>

type IssueTimelineProps = {
  issue: IssueTimelineIssueFragment$key | null
  /** Callers holding the data outright (the Data Router page) pass it instead of a fragment ref. */
  timelineData?: IssueTimelineIssueFragment$data
  /** Cursor the newest window pins to, derived from the page payload by `timeline-prefill`. */
  initialBackAnchor?: string
  issueBodyRef?: RefObject<HTMLDivElement | null>
  issueSecondary?: IssueTimelineSecondaryFragment
  highlightedEvent: string | undefined
  page?: number
  // Passed rather than selected: adding them to the fragment changes `IssueViewerViewQuery` and
  // invalidates every cached issue, and the caller already holds all three.
  owner: string
  repo: string
  number: number
} & EventProps
export const IssueTimeline = ({
  issue,
  timelineData,
  initialBackAnchor,
  issueBodyRef,
  issueSecondary: _issueSecondary,
  viewer,
  highlightedEvent,
  page,
  owner,
  repo,
  number,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  issueUpdatesChannel,
}: IssueTimelineProps) => {
  const fragmentData = useFragment<IssueTimelineIssueFragment$key>(mainTimelineFragment, issue)
  // The header endpoint is the only thing that refreshes on a transfer, so it outranks the
  // secondary query, which no longer hears about one.
  const sharedHeader = useIssueHeaderDataContext()
  // One of the two is always supplied; the fragment read yields null only for the data-router path.
  const data = (timelineData ?? fragmentData) as IssueTimelineIssueFragment$data
  const timelineProps: TimelineSourceProps = {
    data,
    issueBodyRef,
    owner,
    repo,
    number,
    highlightedEvent,
    page,
    isTransferInProgress: sharedHeader.isTransferInProgress,
    viewer,
    onCommentChange,
    onCommentReply,
    onCommentEditCancel,
    optionConfig,
    issueUpdatesChannel,
  }

  return timelineData != null || isFeatureEnabled('issue_viewer_paved_path') ? (
    <TanstackTimeline {...timelineProps} initialBackAnchor={initialBackAnchor} />
  ) : (
    <RelayTimeline {...timelineProps} />
  )
}

type TimelineSourceProps = {
  data: IssueTimelineIssueFragment$data
  issueBodyRef?: RefObject<HTMLDivElement | null>
  owner: string
  repo: string
  number: number
  highlightedEvent: string | undefined
  page?: number
  isTransferInProgress?: boolean
} & EventProps
const RelayTimeline = (props: TimelineSourceProps) => {
  const result = useTimelineItems({
    timelineData: props.data,
    highlightedEvent: props.highlightedEvent,
    page: props.page,
  })

  return <TimelineItemsPanel {...props} {...result} secondaryTimelineMode="relay" />
}

const TanstackTimeline = (props: TimelineSourceProps & {initialBackAnchor?: string}) => {
  const result = useTimelineItemsTanstack({
    owner: props.owner,
    repo: props.repo,
    number: props.number,
    issueId: props.data.id,
    highlightedEvent: props.highlightedEvent,
    page: props.page,
    initialBackAnchor: props.initialBackAnchor,
  })

  return <TimelineItemsPanel {...props} {...result} secondaryTimelineMode="endpoint" />
}

type TimelineItemsPanelProps = TimelineSourceProps &
  UseTimelineItemsCoreResult & {secondaryTimelineMode: 'relay' | 'endpoint'}

const TimelineItemsPanel = ({
  data,
  viewer,
  highlightedEvent,
  page,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  isTransferInProgress,
  issueBodyRef,
  timelineItems,
  totalItemCount,
  highlightedReady,
  newestSectionQueryKey,
  secondaryTimelineMode,
  issueUpdatesChannel,
  owner,
  repo,
  number,
}: TimelineItemsPanelProps) => {
  const preheatMode = useRelayCachePreheatMode()

  return (
    <TimelinePanel
      timelineItems={timelineItems}
      totalItemCount={totalItemCount}
      newestSectionQueryKey={newestSectionQueryKey}
      highlightedEvent={highlightedEvent}
      highlightedReady={highlightedReady}
      issueId={data.id}
      owner={owner}
      repo={repo}
      number={number}
      repositoryId={data.repository.id}
      repositoryNameWithOwner={data.repository.nameWithOwner}
      issueUrl={data.url}
      page={page}
      isTransferInProgress={isTransferInProgress}
      viewer={viewer}
      disablePreheat={secondaryTimelineMode === 'endpoint' && preheatMode !== 'tanstack'}
      issueBodyRef={issueBodyRef}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
      issueUpdatesChannel={issueUpdatesChannel}
      secondaryTimelineMode={secondaryTimelineMode}
    />
  )
}
